import {
  ALERT_TIME_ZONE,
  GEOFENCE_ALARM_TYPES,
  checkRouteWindow,
  cleanRoute,
  routeSegments,
  zonedTimeToUtc,
  buildSiteTree,
  type Alarm,
  type ReportResult,
  type RoutePoint,
} from "@fts/core";
import { Suspense, lazy, useMemo, useRef, useState } from "react";
import { PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { alarmTypeLabel } from "../../lib/alarms";
import { formatDateTimeIn } from "../../lib/export/sheet";
import { durationLabel, routePointsSheet, routeSegmentsSheet } from "../../lib/report-sheets";
import { descendantIds } from "../../lib/fleet";
import { navigate, type Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { ExportBar, Loading, ReportMeta, addDays, filtersQuery, parseFilters, siteOptions, todayLocal, useLoad, type ExportSpec } from "./shared";

const RouteMap = lazy(() => import("./RouteMap"));

const HM = /^([01]\d|2[0-3]):[0-5]\d$|^24:00$/;

type RouteData = { result: ReportResult<RoutePoint>; tz: string; geofence: Alarm[] };

/**
 * Rota raporu (ADR 0018): one vehicle over at most 24 h — map replay, stops (stationary ≥ 2 min), geofence
 * alarms on the route and a table of segments. Times are in the vehicle's site time zone.
 */
export function RouteReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, lang, locale } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const vehicleId = f.vehicleIds[0] ?? null;
  const vehicle = vehicleId ? data.vehicles.find((v) => v.id === vehicleId) : undefined;
  const today = todayLocal();
  const dayQ = route.query.get("day");
  const day = dayQ && /^\d{4}-\d{2}-\d{2}$/.test(dayQ) ? dayQ : today;
  const t0 = HM.test(route.query.get("t0") ?? "") ? route.query.get("t0")! : "00:00";
  const t1 = HM.test(route.query.get("t1") ?? "") ? route.query.get("t1")! : "24:00";
  const tenantId = tenant?.id;
  const [mapError, setMapError] = useState(false);
  const snapshot = useRef<(() => string | null) | null>(null);

  const windowOf = (tz: string) => {
    const from = zonedTimeToUtc(day, t0, tz);
    // An end at or before the start runs into the next day (still at most 24 h).
    const to = t1 === "24:00" || t1 <= t0 ? zonedTimeToUtc(addDays(day, 1), t1 === "24:00" ? "00:00" : t1, tz) : zonedTimeToUtc(day, t1, tz);
    return { from: from.toISOString(), to: to.toISOString() };
  };
  const state = useLoad<RouteData>(
    tenantId && vehicle
      ? async () => {
          const eff = await provider.getEffectiveAlertSettings(vehicle.siteId).catch(() => null);
          const tz = eff?.hours.tz ?? ALERT_TIME_ZONE;
          const { from, to } = windowOf(tz);
          const issue = checkRouteWindow(from, to);
          if (issue) throw new Error(issue);
          const [result, alarms] = await Promise.all([
            provider.reportRoute({ tenantId, vehicleId: vehicle.id, from, to }),
            provider.listAlarms(tenantId, { vehicleId: vehicle.id, from, to, limit: 500 }),
          ]);
          return { result, tz, geofence: alarms.filter((a) => (GEOFENCE_ALARM_TYPES as readonly string[]).includes(a.type)) };
        }
      : null,
    [provider, tenantId, vehicle?.id, day, t0, t1],
  );

  const cleaned = useMemo(() => cleanRoute(state.data?.result.rows ?? []), [state.data]);
  const segments = useMemo(() => routeSegments(cleaned.points), [cleaned]);
  const stops = useMemo(() => segments.filter((s) => s.kind === "stop"), [segments]);
  const events = useMemo(
    () => (state.data?.geofence ?? []).filter((a) => a.lat !== null && a.lon !== null).map((a) => ({ id: a.id, lat: a.lat!, lon: a.lon!, label: alarmTypeLabel(t, a.type) })),
    [state.data, t],
  );
  const tz = state.data?.tz ?? ALERT_TIME_ZONE;
  const withOperators = cleaned.points.some((p) => p.operatorName !== null);
  const drivenM = segments.filter((s) => s.kind === "drive").reduce((n, s) => n + s.distanceM, 0);
  const drivenS = segments.filter((s) => s.kind === "drive").reduce((n, s) => n + s.durationS, 0);
  const options = useMemo(() => siteOptions(buildSiteTree(data.sites, [])), [data.sites]);
  const inSite = f.siteId ? descendantIds(data.sites, f.siteId) : null;
  const vehicles = data.vehicles.filter((v) => !inSite || inSite.has(v.siteId)).sort((a, b) => a.fleetNo.localeCompare(b.fleetNo));
  const update = (patch: Record<string, string | null>) =>
    navigate("/reports/routes", {
      ...filtersQuery({ siteId: f.siteId, vehicleIds: f.vehicleIds }),
      day, t0, t1,
      ...Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, v ?? undefined])),
    });
  const windowLabel = `${day} ${t0}–${t1}`;
  const spec: ExportSpec | null = state.status === "ready" && vehicle && tenantId
    ? {
        report: "route",
        reportName: t("reports.route.title"),
        slug: t("reports.route.slug"),
        part: `${vehicle.fleetNo}_${day}`,
        tz,
        filters: [
          [t("reports.filter.vehicle"), vehicle.fleetNo],
          [t("reports.filter.window"), windowLabel],
          [t("reports.route.drivenDistance"), t("reports.km", { km: (drivenM / 1000).toLocaleString(locale, { maximumFractionDigits: 2 }) })],
          [t("reports.route.stopsCount"), String(stops.length)],
          ...(cleaned.dropped ? ([[t("reports.route.droppedLabel"), String(cleaned.dropped)]] as [string, string][]) : []),
        ],
        rows: cleaned.points.length,
        // Points (CSV/XLSX) and segments; the PDF shows the map and the segments. Data fetched (and logged) again.
        load: async (format) => {
          const r = await provider.exportRoute({ tenantId, vehicleId: vehicle.id, ...windowOf(tz) }, format);
          const pts = cleanRoute(r.rows).points;
          const ops = pts.some((p) => p.operatorName !== null);
          return { sheets: [routePointsSheet(t, pts, ops), routeSegmentsSheet(t, routeSegments(pts), ops)], tz };
        },
        mapImage: () => (snapshot.current ? snapshot.current() : null),
      }
    : null;

  return (
    <>
      <PageHeader title={t("reports.route.title")} subtitle={t("reports.route.subtitle")} eyebrow={t("nav.group.reports")} />
      <div className="filters report-filters" role="group" aria-label={t("reports.filter.label")}>
        <div className="field field-inline">
          <label htmlFor="rr-site">{t("reports.filter.site")}</label>
          <select id="rr-site" value={f.siteId ?? ""} onChange={(e) => navigate("/reports/routes", { ...filtersQuery({ siteId: e.target.value || null }), day, t0, t1 })}>
            <option value="">{t("reports.filter.allSites")}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="rr-vehicle">{t("reports.filter.vehicle")}</label>
          <select id="rr-vehicle" value={vehicleId ?? ""} onChange={(e) => update({ v: e.target.value || null })}>
            <option value="">{t("reports.filter.chooseVehicle")}</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.fleetNo}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="rr-day">{t("reports.filter.day")}</label>
          <input id="rr-day" type="date" value={day} max={today} onChange={(e) => e.target.value && update({ day: e.target.value })} />
        </div>
        <div className="field field-inline">
          <label htmlFor="rr-t0">{t("reports.filter.startTime")}</label>
          <input id="rr-t0" type="time" value={t0} onChange={(e) => HM.test(e.target.value) && update({ t0: e.target.value })} />
        </div>
        <div className="field field-inline">
          <label htmlFor="rr-t1">{t("reports.filter.endTime")}</label>
          <input id="rr-t1" type="time" value={t1 === "24:00" ? "23:59" : t1} onChange={(e) => HM.test(e.target.value) && update({ t1: e.target.value === "23:59" ? "24:00" : e.target.value })} />
        </div>
      </div>
      <p className="report-meta">{t("reports.route.windowHint")}</p>
      {!vehicle ? (
        <Panel>
          <p className="table-empty">{t("reports.route.pickVehicle")}</p>
        </Panel>
      ) : (
        <>
          <ReportMeta tz={state.data ? tz : null} sample={provider.mode === "demo"} />
          <ExportBar spec={spec} disabled={cleaned.points.length === 0} />
          {state.status === "error" && (
            <p className="form-error" role="alert">
              {state.error === "too_long" ? t("reports.range.routeTooLong") : state.error}
            </p>
          )}
          {state.data?.result.truncated && <p className="form-error">{t("reports.truncated", { max: cleaned.points.length })}</p>}
          {state.status === "loading" || state.status === "idle" ? (
            <Loading />
          ) : state.status === "ready" && cleaned.points.length === 0 ? (
            <Panel>
              <p className="table-empty">{t("reports.route.noPoints")}</p>
            </Panel>
          ) : state.status === "ready" ? (
            <>
              <div className="report-totals">
                <div>
                  <span>{t("reports.route.drivenDistance")}</span>
                  <strong>{t("reports.km", { km: (drivenM / 1000).toLocaleString(locale, { maximumFractionDigits: 2 }) })}</strong>
                </div>
                <div>
                  <span>{t("reports.route.drivingTime")}</span>
                  <strong>{durationLabel(t, drivenS)}</strong>
                </div>
                <div>
                  <span>{t("reports.route.stopsCount")}</span>
                  <strong>{stops.length}</strong>
                </div>
                <div>
                  <span>{t("reports.route.points")}</span>
                  <strong>{cleaned.points.length.toLocaleString(locale)}</strong>
                </div>
              </div>
              {cleaned.dropped > 0 && <p className="report-meta">{t("reports.route.dropped", { count: cleaned.dropped })}</p>}
              <Panel title={t("reports.route.map")} flush>
                {mapError ? (
                  <p className="table-empty">{t("reports.route.mapError")}</p>
                ) : (
                  <Suspense fallback={<Loading />}>
                    <RouteMap
                      points={cleaned.points}
                      stops={stops}
                      events={events}
                      label={t("reports.route.mapLabel", { vehicle: vehicle.fleetNo })}
                      startLabel={t("reports.route.start")}
                      endLabel={t("reports.route.end")}
                      onError={() => setMapError(true)}
                      onSnapshot={(take) => (snapshot.current = take)}
                    />
                  </Suspense>
                )}
                <p className="map-legend">
                  <span className="legend-line" /> {t("reports.route.legendTrack")} <span className="legend-dot legend-stop" /> {t("reports.route.legendStop")}{" "}
                  <span className="legend-dot legend-event" /> {t("reports.route.legendEvent")}
                </p>
              </Panel>
              {state.data!.geofence.length > 0 && (
                <Panel title={t("reports.route.geofenceEvents")}>
                  <ul className="plain-list">
                    {state.data!.geofence.map((a) => (
                      <li key={a.id}>
                        <a href={`#/alarms/${encodeURIComponent(a.id)}`} className="subtle-link">
                          {formatDateTimeIn(a.occurredAt, tz, lang)} · {alarmTypeLabel(t, a.type)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}
              <Panel title={t("reports.route.segments")} flush>
                <div className="table-wrap">
                  <table className="table report-table">
                    <thead>
                      <tr>
                        <th scope="col">{t("reports.col.segment")}</th>
                        <th scope="col">{t("reports.col.start")}</th>
                        <th scope="col">{t("reports.col.end")}</th>
                        <th scope="col" className="num">{t("reports.col.duration")}</th>
                        <th scope="col" className="num">{t("reports.col.distanceKm")}</th>
                        <th scope="col" className="num col-hide-sm">{t("reports.col.maxSpeed")}</th>
                        {withOperators && <th scope="col" className="col-hide-md">{t("reports.col.operators")}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {segments.map((s) => (
                        <tr key={s.from}>
                          <td>
                            <span className={`badge tone-${s.kind === "stop" ? "warn" : "info"}`}>{s.kind === "stop" ? t("reports.route.stop") : t("reports.route.drive")}</span>
                          </td>
                          <td className="nowrap">{formatDateTimeIn(s.from, tz, lang)}</td>
                          <td className="nowrap">{formatDateTimeIn(s.to, tz, lang)}</td>
                          <td className="num">{durationLabel(t, s.durationS)}</td>
                          <td className="num">{(s.distanceM / 1000).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="num col-hide-sm">{s.maxSpeedKmh === null ? t("common.empty") : s.maxSpeedKmh.toLocaleString(locale, { maximumFractionDigits: 1 })}</td>
                          {withOperators && <td className="col-hide-md">{s.operators.join(", ") || t("common.empty")}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          ) : null}
        </>
      )}
      <p className="panel-footnote">{t("reports.route.rules")}</p>
    </>
  );
}

